<template>
  <div class="step-panel">
    <!-- Step Header -->
    <div class="step-header">
      <h2>{{ t('Resumo e Confirmação') }}</h2>
    </div>

    <!-- Helper Banner -->
    <div class="helper-banner">
      <i class="pi pi-info-circle"></i>
      <div class="helper-content">
        <strong>{{ t('Revisão Final do Período') }}</strong>
        <ul>
          <li>{{ t('Revise cuidadosamente todos os valores calculados antes de confirmar') }}</li>
          <li>{{ t('Verifique os pagamentos por colaborador, adiantamentos e deduções') }}</li>
          <li>{{ t('Reembolsos aprovados criam despesas separadas (não descontam do salário)') }}</li>
        </ul>
      </div>
    </div>

    <!-- CRITICAL Warning Banner -->
    <div class="critical-warning-banner">
      <i class="pi pi-exclamation-triangle"></i>
      <div class="warning-content">
        <strong>{{ t('ATENÇÃO CRÍTICA') }}</strong>
        <p>{{ t('Esta ação NÃO pode ser desfeita. Ao confirmar, serão criadas as despesas de salário com status "pendente". Certifique-se de que todos os valores estão corretos antes de prosseguir.') }}</p>
      </div>
    </div>

    <!-- Important Notes -->
    <div class="important-notes">
      <div class="notes-header">
        <i class="pi pi-info-circle"></i>
        <h3>{{ t('O que acontece ao confirmar?') }}</h3>
      </div>
      <ul class="notes-list">
        <li>
          <i class="pi pi-check-circle"></i>
          <span>{{ t('Serão criadas despesas de salário para cada colaborador com o valor líquido calculado') }}</span>
        </li>
        <li>
          <i class="pi pi-check-circle"></i>
          <span>{{ t('Reembolsos aprovados criarão despesas separadas (não descontam do salário)') }}</span>
        </li>
        <li>
          <i class="pi pi-check-circle"></i>
          <span>{{ t('O período será marcado como "confirmado" e estará pronto para pagamento') }}</span>
        </li>
        <li>
          <i class="pi pi-check-circle"></i>
          <span>{{ t('Você poderá acessar as despesas criadas através da tela de pagamentos') }}</span>
        </li>
      </ul>
    </div>

    <p class="step-description">
      {{ t('Revise os valores calculados antes de confirmar e criar as despesas de pagamento') }}
    </p>

    <inline-message severity="info" :closable="false" class="info-message">
      <i class="pi pi-info-circle"></i>
      {{ t('Ao confirmar, serão criadas as despesas de salário para cada colaborador.') }}
    </inline-message>

    <div v-if="isLoading" class="loading-container">
      <spinner data-testid="payroll-summary-loading" />
    </div>

    <div v-else class="summary-container">
      <!-- Employee Payments Table -->
      <div class="section-card">
        <div class="section-header">
          <i class="pi pi-users"></i>
          <h4>{{ t('Pagamentos aos Colaboradores') }}</h4>
        </div>

        <DataTable
          :config="usersTableConfig"
          :data="usersWithPayments"
          class="summary-table"
          data-testid="payroll-summary-users-table"
        />

        <!-- Employee Subtotals -->
        <div class="subtotals-panel">
          <div class="subtotal-row">
            <span class="subtotal-label">{{ t('Total Bruto') }}:</span>
            <span class="subtotal-value amount-positive">{{ formatCurrency(totals.gross) }}</span>
          </div>
          <div class="subtotal-row" v-if="totals.advances > 0">
            <span class="subtotal-label">{{ t('Total Adiantamentos') }}:</span>
            <span class="subtotal-value amount-advance">-{{ formatCurrency(totals.advances) }}</span>
          </div>
          <div class="subtotal-row" v-if="totals.deductions > 0">
            <span class="subtotal-label">{{ t('Total Deduções') }}:</span>
            <span class="subtotal-value amount-negative">-{{ formatCurrency(totals.deductions) }}</span>
          </div>
          <div class="subtotal-row highlight">
            <span class="subtotal-label">{{ t('Total Líquido (Salários)') }}:</span>
            <span class="subtotal-value amount-highlight">{{ formatCurrency(totals.netPayable) }}</span>
          </div>
        </div>
      </div>

      <!-- Refunds Section (Separate Despesas) -->
      <div v-if="refundsSummary.accepted.length > 0" class="section-card refunds-section">
        <div class="section-header">
          <i class="pi pi-wallet"></i>
          <h4>{{ t('Reembolsos Aprovados') }}</h4>
          <Badge :value="refundsSummary.accepted.length" severity="success" />
        </div>

        <p class="section-description">
          {{ t('Reembolsos aprovados criam despesas separadas (não descontam do salário)') }}
        </p>

        <DataTable
          :config="refundsTableConfig"
          :data="refundsSummary.accepted"
          class="refunds-table"
          data-testid="payroll-summary-refunds-table"
        />

        <div class="subtotals-panel">
          <div class="subtotal-row highlight refund-total">
            <span class="subtotal-label">{{ t('Total Reembolsos') }}:</span>
            <span class="subtotal-value amount-refund">{{ formatCurrency(refundsSummary.total) }}</span>
          </div>
        </div>
      </div>

      <!-- Grand Total Section -->
      <div class="grand-total-section">
        <div class="grand-total-card">
          <div class="grand-total-row">
            <div class="total-item">
              <span class="total-label">{{ t('Despesas de Salário') }}</span>
              <span class="total-value">{{ formatCurrency(totals.netPayable) }}</span>
            </div>
            <div v-if="refundsSummary.total > 0" class="total-item">
              <span class="total-label">{{ t('Despesas de Reembolso') }}</span>
              <span class="total-value">{{ formatCurrency(refundsSummary.total) }}</span>
            </div>
            <div class="total-item grand">
              <span class="total-label">{{ t('Total Geral de Despesas') }}</span>
              <span class="total-value">{{ formatCurrency((Number(totals.netPayable) || 0) + (Number(refundsSummary.total) || 0)) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Confirmation Warning -->
      <inline-message severity="warn" :closable="false" class="confirmation-warning">
        <div class="warning-content">
          <i class="pi pi-exclamation-triangle"></i>
          <strong>{{ t('Atenção:') }}</strong>
          {{ t('Ao confirmar, serão criadas as despesas com status "pendente".') }}
        </div>
      </inline-message>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, inject, type ComputedRef, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePayrollRefunds } from '@/composables/usePayrollRefunds';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';
import { permissionsService } from '@/services/permissionsService';

type Id = number | string;

interface UserPaymentRow {
  user_id?: Id;
  user_name?: string;
  gross_total?: number | string;
  [key: string]: unknown;
}

interface DiscountEntry {
  advance_id?: Id;
  deduction_id?: Id;
  amount?: number | string;
  employee_id?: Id;
  employee_name?: string;
  [key: string]: unknown;
}

interface RefundRow {
  id?: Id;
  title?: string;
  pays_to?: Id;
  amount?: number | string;
  [key: string]: unknown;
}

interface UserWithPayment extends UserPaymentRow {
  gross_total: number;
  deductions: { total: number; count: number; items: DiscountEntry[] };
  advances: { total: number; count: number };
  net_payable: number;
}

interface CellParams {
  data: Record<string, unknown>;
  value?: unknown;
}

interface WizardMachineContext {
  period?: { id?: Id; franchise_id?: Id } | null;
  periodFormData?: { franchise_id?: Id } | null;
  paymentByUser?: UserPaymentRow[];
  advanceDiscounts?: DiscountEntry[];
  deductionDiscounts?: DiscountEntry[];
  advanceDiscountsTotal?: number;
  deductionDiscountsTotal?: number;
  [key: string]: unknown;
}

interface WizardMachine {
  context: Ref<WizardMachineContext> | ComputedRef<WizardMachineContext>;
  [key: string]: unknown;
}

// =============================================================================
// INJECT MACHINE - Single Source of Truth
// =============================================================================

const machine = inject<WizardMachine>('wizardMachine') as WizardMachine;

// =============================================================================
// REACTIVE DATA FROM MACHINE
// =============================================================================

/**
 * Get period ID from machine context (converted to Number for prop type safety)
 */
const periodId = computed<number | null>(() => {
  const id = machine.context.value.period?.id;
  return id ? Number(id) : null;
});

/**
 * Get franchise ID from machine context
 */
const franchiseId = computed<number | null>(() => {
  return Number(machine.context.value.period?.franchise_id) ||
         Number(machine.context.value.periodFormData?.franchise_id) ||
         null;
});

/**
 * Get payment by user data from machine context
 */
const byUser = computed<UserPaymentRow[]>(() => {
  return machine.context.value.paymentByUser || [];
});

/**
 * Build users map from payment by user data
 */
const usersMap = computed<Record<string, { name: string }>>(() => {
  const users = machine.context.value.paymentByUser || [];
  const map: Record<string, { name: string }> = {};
  users.forEach(u => {
    if (u.user_id !== undefined && u.user_id !== null) {
      map[String(u.user_id)] = { name: u.user_name || `User ${u.user_id}` };
    }
  });
  return map;
});

const { t, n } = useI18n();
const { listRefunds } = usePayrollRefunds();

// =============================================================================
// NEW: Get advances and deductions from machine context (not API)
// =============================================================================

/**
 * Get advance discounts from machine context
 * These are stored as { advance_id, amount, employee_id, employee_name, ... }
 */
const advanceDiscountsFromMachine = computed<DiscountEntry[]>(() => {
  return machine.context.value.advanceDiscounts || [];
});

/**
 * Get deduction discounts from machine context
 * These are stored as { deduction_id, amount, employee_id, employee_name, ... }
 */
const deductionDiscountsFromMachine = computed<DiscountEntry[]>(() => {
  return machine.context.value.deductionDiscounts || [];
});

/**
 * Get total advance discounts from machine context
 */
const advanceDiscountsTotalFromMachine = computed<number>(() => {
  return machine.context.value.advanceDiscountsTotal || 0;
});

/**
 * Get total deduction discounts from machine context
 */
const deductionDiscountsTotalFromMachine = computed<number>(() => {
  return machine.context.value.deductionDiscountsTotal || 0;
});

// Helper function to safely format currency - ensures we always pass a valid number to n()
const formatCurrency = (value: unknown): string => {
  const num = Number(parseFloat(String(value ?? 0))) || 0;
  if (isNaN(num)) {
    log('StepPayrollSummary', 'formatCurrency', 'Invalid number detected', { value, num });
    return n(0, 'currency');
  }
  return n(num, 'currency');
};

const isLoading = ref<boolean>(false);
const acceptedRefunds = ref<RefundRow[]>([]);

// Merge byUser data with deductions and advances from machine context
const usersWithPayments = computed<UserWithPayment[]>(() => {
  // Guard: ensure byUser is an array
  if (!Array.isArray(byUser.value)) {
    return [];
  }

  // Get advances and deductions from machine context
  const advanceDiscounts = advanceDiscountsFromMachine.value;
  const deductionDiscounts = deductionDiscountsFromMachine.value;

  log('StepPayrollSummary', 'usersWithPayments', 'Computing with machine context', {
    byUserCount: byUser.value.length,
    advanceDiscountsCount: advanceDiscounts.length,
    deductionDiscountsCount: deductionDiscounts.length,
    advanceDiscounts: advanceDiscounts.map(a => ({ id: a.advance_id, amount: a.amount, employee_id: a.employee_id })),
    deductionDiscounts: deductionDiscounts.map(d => ({ id: d.deduction_id, amount: d.amount, employee_id: d.employee_id }))
  });

  return byUser.value.map((user): UserWithPayment | null => {
    // Guard: ensure user is an object
    if (!user || typeof user !== 'object') {
      return null;
    }

    // Find deductions for this user from machine context
    // NEW: employee_id in deductionDiscounts matches user_id in byUser
    const userDeductions = deductionDiscounts.filter(d => d && d.employee_id === user.user_id);
    const deductionsTotal = Number(userDeductions.reduce((sum, d) => sum + (parseFloat(String(d?.amount ?? 0)) || 0), 0)) || 0;

    // Find advances for this user from machine context
    // NEW: employee_id in advanceDiscounts matches user_id in byUser
    const userAdvances = advanceDiscounts.filter(a => a && a.employee_id === user.user_id);
    const advancesTotal = Number(userAdvances.reduce((sum, a) => sum + (parseFloat(String(a?.amount ?? 0)) || 0), 0)) || 0;
    const advancesCount = userAdvances.length;

    const grossTotal = Number(parseFloat(String(user.gross_total ?? 0)) || 0);
    const netPayable = Number(grossTotal - deductionsTotal - advancesTotal) || 0;

    log('StepPayrollSummary', 'usersWithPayments', 'User calculation', {
      user_id: user.user_id,
      user_name: user.user_name,
      grossTotal,
      deductionsTotal,
      advancesTotal,
      netPayable,
      userDeductionsCount: userDeductions.length,
      userAdvancesCount: advancesCount
    });

    return {
      ...user,
      gross_total: grossTotal,
      deductions: {
        total: deductionsTotal,
        count: userDeductions.length,
        items: userDeductions
      },
      advances: {
        total: advancesTotal,
        count: advancesCount
      },
      net_payable: netPayable
    };
  }).filter((user): user is UserWithPayment => user !== null); // Remove any null entries
});

// Totals - ensure all values are valid numbers (not NaN)
const totals = computed<{ gross: number; advances: number; deductions: number; netPayable: number }>(() => {
  const gross = Number(usersWithPayments.value.reduce((sum, user) => sum + (parseFloat(String(user.gross_total ?? 0)) || 0), 0)) || 0;
  const advances = Number(usersWithPayments.value.reduce((sum, user) => sum + (parseFloat(String(user.advances?.total ?? 0)) || 0), 0)) || 0;
  const deductions = Number(usersWithPayments.value.reduce((sum, user) => sum + (parseFloat(String(user.deductions?.total ?? 0)) || 0), 0)) || 0;
  const netPayable = Number(gross - advances - deductions) || 0;

  return { gross, advances, deductions, netPayable };
});

// Refunds summary - calculate total from accepted refunds list
const refundsSummary = computed<{ accepted: RefundRow[]; total: number }>(() => {
  const accepted = acceptedRefunds.value || [];
  const total = Number(accepted.reduce((sum, refund) => sum + (parseFloat(String(refund.amount ?? 0)) || 0), 0)) || 0;
  return { accepted, total };
});

// Get user name from usersMap or permissionsService
const getUserName = (userId: Id | null | undefined): string => {
  if (userId === null || userId === undefined) return t('Desconhecido');
  const key = String(userId);
  const mapped = usersMap.value[key] as { name?: string; user_name?: string } | undefined;
  return mapped?.name ||
         mapped?.user_name ||
         permissionsService.getUserNameBySub(String(userId)) ||
         String(userId);
};

// =============================================================================
// DataTable configs (config-driven mode)
// =============================================================================

const usersTableConfig = computed(() => ({
  tableId: 'payroll-summary-users',
  columns: [
    {
      data: 'user_name',
      title: t('Colaborador'),
      sortable: true,
      cellRenderer: (params: CellParams) => {
        const data = params.data as UserWithPayment;
        const name = data?.user_name || t('Desconhecido');
        const container = document.createElement('div');
        container.className = 'user-info';
        container.innerHTML = `<i class="pi pi-user"></i><span class="user-name">${escapeHtml(String(name))}</span>`;
        return container;
      }
    },
    {
      data: 'gross_total',
      title: t('Total Bruto'),
      sortable: true,
      cellRenderer: (params: CellParams) => {
        const data = params.data as UserWithPayment | undefined;
        const value = data?.gross_total ?? 0;
        return `<span class="amount-positive">${formatCurrency(value)}</span>`;
      }
    },
    {
      data: 'advances',
      title: t('Adiantamentos'),
      sortable: false,
      cellRenderer: (params: CellParams) => {
        const data = params.data as UserWithPayment | undefined;
        const total = data?.advances?.total ?? 0;
        const count = data?.advances?.count ?? 0;
        const zero = !total ? ' zero' : '';
        const badge = count > 0
          ? `<span class="p-badge p-component p-badge-warning count-badge">${count}</span>`
          : '';
        return `<div class="deduction-cell"><span class="amount-advance${zero}">-${formatCurrency(total)}</span>${badge}</div>`;
      }
    },
    {
      data: 'deductions',
      title: t('Deduções'),
      sortable: false,
      cellRenderer: (params: CellParams) => {
        const data = params.data as UserWithPayment | undefined;
        const total = data?.deductions?.total ?? 0;
        const count = data?.deductions?.count ?? 0;
        const zero = !total ? ' zero' : '';
        const badge = count > 0
          ? `<span class="p-badge p-component p-badge-info count-badge">${count}</span>`
          : '';
        return `<div class="deduction-cell"><span class="amount-negative${zero}">-${formatCurrency(total)}</span>${badge}</div>`;
      }
    },
    {
      data: 'net_payable',
      title: t('Valor Líquido'),
      sortable: true,
      cellRenderer: (params: CellParams) => {
        const data = params.data as UserWithPayment | undefined;
        const value = data?.net_payable ?? 0;
        return `<span class="amount-highlight">${formatCurrency(value)}</span>`;
      }
    }
  ],
  pagination: false
}));

const refundsTableConfig = computed(() => ({
  tableId: 'payroll-summary-refunds',
  columns: [
    {
      data: 'title',
      title: t('Descrição'),
      cellRenderer: (params: CellParams) => {
        const data = params.data as RefundRow | undefined;
        const title = data?.title || t('Sem descrição');
        return escapeHtml(String(title));
      }
    },
    {
      data: 'pays_to',
      title: t('Beneficiário'),
      cellRenderer: (params: CellParams) => {
        const data = params.data as RefundRow | undefined;
        const name = getUserName(data?.pays_to);
        const container = document.createElement('div');
        container.className = 'user-info';
        container.innerHTML = `<i class="pi pi-user"></i><span>${escapeHtml(String(name))}</span>`;
        return container;
      }
    },
    {
      data: 'amount',
      title: t('Valor'),
      cellRenderer: (params: CellParams) => {
        const data = params.data as RefundRow | undefined;
        const value = data?.amount ?? 0;
        return `<span class="amount-refund">${formatCurrency(value)}</span>`;
      }
    }
  ],
  pagination: false
}));

// HTML-escape helper for safe cellRenderer string output
const escapeHtml = (input: string): string => {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

// Load refunds data (advances and deductions come from machine context)
const loadData = async (): Promise<void> => {
  log('StepPayrollSummary', 'loadData', 'Called', {
    periodId: periodId.value,
    periodIdType: typeof periodId.value,
    isNaN: isNaN(Number(periodId.value)),
    willSkip: !periodId.value || isNaN(Number(periodId.value)),
    advanceDiscountsCount: advanceDiscountsFromMachine.value.length,
    deductionDiscountsCount: deductionDiscountsFromMachine.value.length,
    advanceDiscountsTotal: advanceDiscountsTotalFromMachine.value,
    deductionDiscountsTotal: deductionDiscountsTotalFromMachine.value
  });

  // Guard: only load if we have a valid periodId
  if (!periodId.value || isNaN(Number(periodId.value))) {
    log('StepPayrollSummary', 'loadData', 'Skipping - invalid periodId', { periodId: periodId.value });
    return;
  }

  isLoading.value = true;
  try {
    log('StepPayrollSummary', 'loadData', 'Loading refunds data', { periodId: periodId.value });

    // Only load refunds from API - advances and deductions come from machine context
    // For refunds, we use listRefunds with status=1 (accepted) and exclude_paid=true
    // to only show unpaid refunds (refunds where the linked despesa is not yet "baixada")
    const refundsData = await listRefunds({ period_id: periodId.value, status: 1, exclude_paid: true });

    acceptedRefunds.value = Array.isArray(refundsData) ? refundsData : [];

    log('StepPayrollSummary', 'loadData', 'Data loaded successfully', {
      acceptedRefunds: acceptedRefunds.value.length,
      // Log machine context data for debugging
      advanceDiscountsFromContext: advanceDiscountsFromMachine.value.map(a => ({
        advance_id: a.advance_id,
        amount: a.amount,
        employee_id: a.employee_id
      })),
      deductionDiscountsFromContext: deductionDiscountsFromMachine.value.map(d => ({
        deduction_id: d.deduction_id,
        amount: d.amount,
        employee_id: d.employee_id
      }))
    });
  } catch (error) {
    console.error('Error loading summary data:', error);
    eventBus.emit('globalMessage.error', {
      message: (error as Error)?.message || t('Erro ao carregar dados do resumo'),
      duration: 5000
    });
  } finally {
    isLoading.value = false;
  }
};

// Lifecycle
onMounted(() => {
  log('StepPayrollSummary', 'onMounted', 'Component mounted', {
    periodId: periodId.value,
    periodIdType: typeof periodId.value,
    franchiseId: franchiseId.value,
    byUserLength: byUser.value?.length,
    // Log machine context data
    advanceDiscountsCount: advanceDiscountsFromMachine.value.length,
    deductionDiscountsCount: deductionDiscountsFromMachine.value.length,
    advanceDiscountsTotal: advanceDiscountsTotalFromMachine.value,
    deductionDiscountsTotal: deductionDiscountsTotalFromMachine.value
  });
  loadData();
});

// Watch for period changes - only reload when periodId becomes valid
watch(() => periodId.value, (newVal, oldVal) => {
  if (newVal && !isNaN(Number(newVal)) && newVal !== oldVal) {
    loadData();
  }
});
</script>

<style scoped>
/* Step Header */
.step-header h2 {
  margin: 0 0 1rem 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
}

/* Helper Banner */
.helper-banner {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: var(--blue-50);
  border-left: 4px solid var(--blue-500);
  border-radius: 6px;
  margin-bottom: 1rem;
}

.helper-banner i {
  color: var(--blue-500);
  font-size: 1.5rem;
  flex-shrink: 0;
}

.helper-content strong {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--blue-700);
}

.helper-content ul {
  margin: 0;
  padding-left: 1.25rem;
}

.helper-content li {
  margin-bottom: 0.25rem;
  color: var(--blue-900);
  font-size: 0.9rem;
}

/* Critical Warning Banner */
.critical-warning-banner {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: var(--red-50);
  border-left: 4px solid var(--red-500);
  border-radius: 6px;
  margin-bottom: 1rem;
}

.critical-warning-banner i {
  color: var(--red-500);
  font-size: 1.5rem;
  flex-shrink: 0;
}

.critical-warning-banner .warning-content strong {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--red-700);
  font-size: 1rem;
  font-weight: 700;
}

.critical-warning-banner .warning-content p {
  margin: 0;
  color: var(--red-900);
  font-size: 0.9rem;
  line-height: 1.5;
}

/* Important Notes */
.important-notes {
  padding: 1.25rem;
  background: var(--surface-0);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  margin-bottom: 1rem;
}

.notes-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--surface-border);
}

.notes-header i {
  font-size: 1.25rem;
  color: var(--primary-color);
}

.notes-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
}

.notes-list {
  margin: 0;
  padding-left: 0;
  list-style: none;
}

.notes-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
  color: var(--text-color);
  line-height: 1.5;
}

.notes-list li i {
  color: var(--primary-color);
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.step-panel {
  padding: 1.5rem;
}

.step-description {
  color: var(--text-color-secondary);
  margin-bottom: 1rem;
}

.info-message {
  margin-bottom: 1.5rem;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
}

.summary-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Section Cards */
.section-card {
  background: var(--surface-card);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
  overflow: hidden;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: var(--surface-50);
  border-bottom: 1px solid var(--surface-border);
}

.section-header i {
  font-size: 1.25rem;
  color: var(--primary-color);
}

.section-header h4 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  flex: 1;
}

.section-description {
  padding: 0.75rem 1.5rem;
  margin: 0;
  background: var(--blue-50);
  color: var(--blue-800);
  font-size: 0.9rem;
  border-bottom: 1px solid var(--surface-border);
}

/* Refunds Section */
.refunds-section .section-header i {
  color: var(--teal-600);
}

/* Tables */
.summary-table,
.refunds-table {
  border: none;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.user-info i {
  color: var(--text-color-secondary);
}

.user-name {
  font-weight: 500;
}

.deduction-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.count-badge {
  font-size: 0.7rem;
}

/* Amount Styling */
.amount-positive {
  color: var(--green-600);
  font-weight: 600;
}

.amount-negative {
  color: var(--red-600);
  font-weight: 600;
}

.amount-advance {
  color: var(--orange-600);
  font-weight: 600;
}

.amount-refund {
  color: var(--teal-600);
  font-weight: 600;
}

.amount-highlight {
  color: var(--primary-color);
  font-weight: 700;
  font-size: 1.05rem;
}

.amount-negative.zero,
.amount-advance.zero {
  color: var(--text-color-secondary);
  font-weight: 400;
}

/* Subtotals Panel */
.subtotals-panel {
  padding: 1rem 1.5rem;
  background: var(--surface-50);
  border-top: 1px solid var(--surface-border);
}

.subtotal-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
}

.subtotal-row:not(:last-child) {
  border-bottom: 1px dashed var(--surface-200);
}

.subtotal-row.highlight {
  padding-top: 0.75rem;
  margin-top: 0.5rem;
  border-top: 2px solid var(--surface-300);
}

.subtotal-label {
  font-weight: 500;
  color: var(--text-color-secondary);
}

.subtotal-row.highlight .subtotal-label {
  font-weight: 600;
  color: var(--text-color);
}

.subtotal-value {
  font-size: 1.1rem;
}

.subtotal-row.highlight .subtotal-value {
  font-size: 1.25rem;
}

.refund-total .subtotal-value {
  color: var(--teal-600);
}

/* Grand Total Section */
.grand-total-section {
  margin-top: 0.5rem;
}

.grand-total-card {
  background: linear-gradient(135deg, var(--primary-50) 0%, var(--surface-card) 100%);
  border: 2px solid var(--primary-200);
  border-radius: 12px;
  padding: 1.5rem;
}

.grand-total-row {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  justify-content: center;
}

.total-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: var(--surface-card);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
  min-width: 180px;
}

.total-item.grand {
  background: var(--primary-color);
  border-color: var(--primary-color);
}

.total-item .total-label {
  font-size: 0.9rem;
  color: var(--text-color-secondary);
  font-weight: 500;
}

.total-item.grand .total-label {
  color: rgba(255, 255, 255, 0.9);
}

.total-item .total-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
}

.total-item.grand .total-value {
  color: white;
  font-size: 1.75rem;
}

/* Confirmation Warning */
.confirmation-warning {
  margin-top: 1rem;
}

.warning-content {
  line-height: 1.5;
}

.warning-content strong {
  display: block;
  margin-bottom: 0.25rem;
}

/* Responsive */
@media (max-width: 768px) {
  .grand-total-row {
    flex-direction: column;
    align-items: stretch;
  }

  .total-item {
    min-width: auto;
  }
}
</style>
